import { useCallback, useEffect, useReducer } from 'react';
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { useToast } from '@/hooks/use-toast';
import {
  useGravarCampos,
  useListarCampos,
  type CampoApi,
  type CampoPayload,
} from '@/services/Relatorios';
import { configuracaoCamposReducer, initialState } from './configuracaoCamposReducer';

interface UseConfiguracaoCamposProps {
  relatorioId: string | null;
  onSaveSuccess?: () => void;
}

export function useConfiguracaoCampos({ relatorioId, onSaveSuccess }: UseConfiguracaoCamposProps) {
  const { toast } = useToast();
  const { data: campos, isLoading } = useListarCampos(relatorioId);
  const { mutateAsync: gravarCampos, isPending: isSaving } = useGravarCampos();
  const [state, dispatch] = useReducer(configuracaoCamposReducer, initialState);

  useEffect(() => {
    if (!campos) return;

    const visiveis = campos
      .filter(campo => campo.Configurado === 'S')
      .sort((a, b) => a.Sequencia_Campo - b.Sequencia_Campo);
    const excluidos = campos
      .filter(campo => campo.Configurado === 'N')
      .sort((a, b) => a.Sequencia_Campo - b.Sequencia_Campo);
    const selectedIds = new Set(
      visiveis.filter(campo => campo.Sequencia_Separacao !== null).map(campo => campo.Campo_id)
    );

    dispatch({ type: 'INIT_CAMPOS', visiveis, excluidos, selectedIds });
  }, [campos]);

  const activeCampo = state.activeId
    ? ([
        ...state.camposVisiveis,
        ...state.camposExcluidos,
        ...state.camposSeparacao,
        ...state.camposOrdenacao,
        ...state.totalizadoresIncluidos,
        ...state.totalizadoresExcluidos,
      ].find(campo => campo.Campo_id === state.activeId) ?? null)
    : null;

  const activeIsVisivel = state.activeId
    ? state.passo === 1 && state.camposVisiveis.some(campo => campo.Campo_id === state.activeId)
    : false;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    dispatch({ type: 'SET_ACTIVE_ID', id: event.active.id as number });
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeId = active.id as number;
      const overId = over.id as number;

      if (state.passo === 1) {
        const activeInVisiveis = state.camposVisiveis.some(campo => campo.Campo_id === activeId);
        const overInVisiveis =
          state.camposVisiveis.some(campo => campo.Campo_id === overId) ||
          over.id === 'visiveis-droppable';
        const overInExcluidos =
          state.camposExcluidos.some(campo => campo.Campo_id === overId) ||
          over.id === 'excluidos-droppable';

        if (activeInVisiveis && overInExcluidos) {
          dispatch({ type: 'MOVE_TO_EXCLUIDOS', campoId: activeId });
        } else if (!activeInVisiveis && overInVisiveis) {
          dispatch({ type: 'MOVE_TO_VISIVEIS', campoId: activeId });
        }
      }

      if (state.passo === 2) {
        const activeInSeparacao = state.camposSeparacao.some(campo => campo.Campo_id === activeId);
        const overInSeparacao =
          state.camposSeparacao.some(campo => campo.Campo_id === overId) ||
          over.id === 'separacao-droppable';
        const overInOrdenacao =
          state.camposOrdenacao.some(campo => campo.Campo_id === overId) ||
          over.id === 'ordenacao-droppable';

        if (activeInSeparacao && overInOrdenacao) {
          dispatch({ type: 'MOVE_TO_ORDENACAO', campoId: activeId });
        } else if (!activeInSeparacao && overInSeparacao) {
          dispatch({ type: 'MOVE_TO_SEPARACAO', campoId: activeId });
        }
      }

      if (state.passo === 3) {
        const activeInIncluidos = state.totalizadoresIncluidos.some(
          campo => campo.Campo_id === activeId
        );
        const activeInExcluidos = state.totalizadoresExcluidos.some(
          campo => campo.Campo_id === activeId
        );
        const overInIncluidos =
          state.totalizadoresIncluidos.some(campo => campo.Campo_id === overId) ||
          over.id === 'totalizadores-incluidos-droppable';
        const overInExcluidos =
          state.totalizadoresExcluidos.some(campo => campo.Campo_id === overId) ||
          over.id === 'totalizadores-excluidos-droppable';

        if (activeInIncluidos && overInExcluidos) {
          dispatch({ type: 'MOVE_TO_TOTALIZADORES_EXCLUIDOS', campoId: activeId });
        } else if (activeInExcluidos && overInIncluidos) {
          dispatch({ type: 'MOVE_TO_TOTALIZADORES_INCLUIDOS', campoId: activeId });
        }
      }
    },
    [
      state.camposExcluidos,
      state.camposOrdenacao,
      state.camposSeparacao,
      state.camposVisiveis,
      state.passo,
      state.totalizadoresExcluidos,
      state.totalizadoresIncluidos,
    ]
  );

  const tentarReordenar = useCallback(
    (
      listName:
        | 'camposVisiveis'
        | 'camposExcluidos'
        | 'camposSeparacao'
        | 'camposOrdenacao'
        | 'totalizadoresIncluidos'
        | 'totalizadoresExcluidos',
      activeId: number,
      overId: number
    ) => {
      const list = state[listName];
      if (
        list.some(campo => campo.Campo_id === activeId) &&
        list.some(campo => campo.Campo_id === overId)
      ) {
        dispatch({ type: 'REORDER_LIST', listName, activeId, overId });
        return true;
      }

      return false;
    },
    [state]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      dispatch({ type: 'SET_ACTIVE_ID', id: null });

      if (!over) return;

      const activeId = active.id as number;
      const overId = over.id;

      if (state.passo === 1) {
        if (overId === 'visiveis-droppable') {
          dispatch({ type: 'MOVE_TO_VISIVEIS', campoId: activeId });
          return;
        }

        if (overId === 'excluidos-droppable') {
          dispatch({ type: 'MOVE_TO_EXCLUIDOS', campoId: activeId });
          return;
        }
      }

      if (state.passo === 2) {
        if (overId === 'separacao-droppable') {
          dispatch({ type: 'MOVE_TO_SEPARACAO', campoId: activeId });
          return;
        }

        if (overId === 'ordenacao-droppable') {
          dispatch({ type: 'MOVE_TO_ORDENACAO', campoId: activeId });
          return;
        }
      }

      if (state.passo === 3) {
        if (overId === 'totalizadores-incluidos-droppable') {
          dispatch({ type: 'MOVE_TO_TOTALIZADORES_INCLUIDOS', campoId: activeId });
          return;
        }

        if (overId === 'totalizadores-excluidos-droppable') {
          dispatch({ type: 'MOVE_TO_TOTALIZADORES_EXCLUIDOS', campoId: activeId });
          return;
        }
      }

      const overCampoId = typeof overId === 'number' ? overId : Number(overId);
      if (!Number.isFinite(overCampoId)) return;

      if (activeId === overCampoId) return;

      if (state.passo === 1) {
        if (!tentarReordenar('camposVisiveis', activeId, overCampoId)) {
          tentarReordenar('camposExcluidos', activeId, overCampoId);
        }
      } else if (state.passo === 2) {
        if (!tentarReordenar('camposSeparacao', activeId, overCampoId)) {
          tentarReordenar('camposOrdenacao', activeId, overCampoId);
        }
      } else if (state.passo === 3) {
        if (!tentarReordenar('totalizadoresIncluidos', activeId, overCampoId)) {
          tentarReordenar('totalizadoresExcluidos', activeId, overCampoId);
        }
      }
    },
    [state.passo, tentarReordenar]
  );

  const toggleSelection = useCallback((campoId: number) => {
    dispatch({ type: 'TOGGLE_SELECTION', campoId });
  }, []);

  const handleAvancar = useCallback(() => {
    if (state.passo === 1) {
      const separacao = state.camposVisiveis.filter(campo =>
        state.selectedItems.has(campo.Campo_id)
      );
      const ordenacao = state.camposVisiveis.filter(
        campo => !state.selectedItems.has(campo.Campo_id)
      );
      dispatch({ type: 'AVANCAR_PASSO_2', separacao, ordenacao });
      return;
    }

    if (state.passo === 2) {
      const todosCampos = [
        ...state.camposSeparacao,
        ...state.camposOrdenacao,
        ...state.camposExcluidos,
      ];
      const incluidos = todosCampos.filter(campo => campo.Totalizador === 'S');
      const excluidos = todosCampos.filter(campo => campo.Totalizador === 'N');
      const visiveis = [...state.camposSeparacao, ...state.camposOrdenacao];
      dispatch({ type: 'AVANCAR_PASSO_3', incluidos, excluidos, visiveis });
    }
  }, [
    state.camposExcluidos,
    state.camposOrdenacao,
    state.camposSeparacao,
    state.camposVisiveis,
    state.passo,
    state.selectedItems,
  ]);

  const handleVoltar = useCallback(() => {
    dispatch({ type: 'VOLTAR' });
  }, []);

  const setCondicaoTotalizador = useCallback((campoId: number | null) => {
    dispatch({ type: 'SET_CONDICAO_TOTALIZADOR', campoId });
  }, []);

  const montarPayloadVisiveis = useCallback(
    (
      camposSeparacao: CampoApi[],
      camposOrdenacao: CampoApi[],
      totalizadorMap: Map<number, boolean>,
      condicaoTotalizadorCampoId: number | null
    ) => {
      const payload: CampoPayload[] = [];
      const ehCondicao = (campoId: number) => campoId === condicaoTotalizadorCampoId;

      camposSeparacao.forEach((campo, index) => {
        payload.push({
          Campo_id: String(campo.Campo_id),
          Sequencia_Separacao: String(index),
          Ordenacao: '',
          Configurado: true,
          Totalizador: totalizadorMap.get(campo.Campo_id) ?? false,
          CondicaoTotalizador: ehCondicao(campo.Campo_id),
        });
      });

      camposOrdenacao.forEach(campo => {
        payload.push({
          Campo_id: String(campo.Campo_id),
          Sequencia_Separacao: '',
          Ordenacao: '',
          Configurado: true,
          Totalizador: totalizadorMap.get(campo.Campo_id) ?? false,
          CondicaoTotalizador: ehCondicao(campo.Campo_id),
        });
      });

      return payload;
    },
    []
  );

  const handleSalvar = useCallback(async () => {
    if (!relatorioId) return;

    const totalizadorMap = new Map<number, boolean>();
    state.totalizadoresIncluidos.forEach(campo => totalizadorMap.set(campo.Campo_id, true));
    state.totalizadoresExcluidos.forEach(campo => totalizadorMap.set(campo.Campo_id, false));

    const camposVisiveisPayload = montarPayloadVisiveis(
      state.camposSeparacao,
      state.camposOrdenacao,
      totalizadorMap,
      state.condicaoTotalizadorCampoId
    );

    const camposExcluidosPayload: CampoPayload[] = state.camposExcluidos.map(campo => ({
      Campo_id: String(campo.Campo_id),
      Sequencia_Separacao: '',
      Ordenacao: '',
      Configurado: false,
      Totalizador: totalizadorMap.get(campo.Campo_id) ?? false,
      CondicaoTotalizador: false,
    }));

    try {
      await gravarCampos({
        relatorioId,
        campos: [...camposVisiveisPayload, ...camposExcluidosPayload],
      });
      onSaveSuccess?.();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar configuração',
        description:
          error instanceof Error
            ? error.message
            : 'Não foi possível salvar a configuração do relatório.',
      });
    }
  }, [
    gravarCampos,
    montarPayloadVisiveis,
    onSaveSuccess,
    relatorioId,
    state.camposExcluidos,
    state.camposOrdenacao,
    state.camposSeparacao,
    state.condicaoTotalizadorCampoId,
    state.totalizadoresExcluidos,
    state.totalizadoresIncluidos,
    toast,
  ]);

  return {
    isLoading,
    isSaving,
    passo: state.passo,
    camposVisiveis: state.camposVisiveis,
    camposExcluidos: state.camposExcluidos,
    selectedItems: state.selectedItems,
    camposSeparacao: state.camposSeparacao,
    camposOrdenacao: state.camposOrdenacao,
    totalizadoresIncluidos: state.totalizadoresIncluidos,
    totalizadoresExcluidos: state.totalizadoresExcluidos,
    camposCondicaoDisponiveis: state.camposCondicaoDisponiveis,
    condicaoTotalizadorCampoId: state.condicaoTotalizadorCampoId,
    activeCampo,
    activeIsVisivel,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    toggleSelection,
    handleAvancar,
    handleVoltar,
    handleSalvar,
    setCondicaoTotalizador,
  };
}
